import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { getPublicStandards, submitApplication, getPricing } from '../lib/api';
import { setAuth } from '../lib/auth';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import MainHeader from '../components/MainHeader';
import MainFooter from '../components/MainFooter';
import { Shield, FileText, ExternalLink, CheckCircle, AlertTriangle, ArrowRight, ArrowLeft, List, RefreshCw } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Apply() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [standards, setStandards] = useState([]);
  const [pricing, setPricing] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Form data
  const [formData, setFormData] = useState({
    legal_name: '',
    display_name: '',
    website: '',
    contact_name: '',
    contact_email: '',
    password: '',
    confirmPassword: '',
    standard_ids: [],
    // Declaration documentation fields
    scope_statement: '',
    responsibility_domain: '',
    escalation_structure: '',
    incident_response_doc: '',
    change_control_doc: '',
    client_disclosure_ack: false,
    // Accountability Boundaries
    accountability_scope_doc_url: '',
    accountability_domains_doc_url: '',
    accountability_client_ack_url: '',
    production_deployment_date: ''
  });
  
  // Acknowledgments
  const [acknowledgments, setAcknowledgments] = useState({
    reviewed_standards: false,
    understood_limitations: false,
    accept_terms: false
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [standardsRes, pricingRes] = await Promise.all([
          getPublicStandards(),
          getPricing()
        ]);
        setStandards(standardsRes.data);
        setPricing(pricingRes.data);
        // Auto-select all standards for single-tier model
        setFormData(prev => ({
          ...prev,
          standard_ids: standardsRes.data.map(s => s.id)
        }));
      } catch (err) {
        console.error('Failed to fetch data:', err);
      }
    };
    fetchData();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    setError('');
    
    // Validate form
    if (!formData.legal_name || !formData.display_name || !formData.contact_email || !formData.password) {
      setError('Please fill in all required fields.');
      return;
    }
    
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    
    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    
    if (!acknowledgments.reviewed_standards || !acknowledgments.understood_limitations || !acknowledgments.accept_terms) {
      setError('Please accept all acknowledgments before proceeding.');
      return;
    }
    
    // Validate declaration fields
    if (!formData.scope_statement || !formData.responsibility_domain || !formData.escalation_structure ||
        !formData.incident_response_doc || !formData.change_control_doc || !formData.client_disclosure_ack) {
      setError('Please complete all documentation fields.');
      return;
    }
    
    setLoading(true);
    
    try {
      const applicationData = {
        ...formData,
        application_tier: 'declaration' // Single tier maps to declaration
      };
      delete applicationData.confirmPassword;
      
      const response = await submitApplication(applicationData);
      
      // Store auth token and redirect to payment
      if (response.data.access_token) {
        setAuth(response.data.access_token);
      }
      
      navigate('/payment');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to submit application. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9FAFB]" style={{ fontFamily: 'Manrope, sans-serif' }}>
      <MainHeader />

      <main className="py-16 px-6">
        <div className="max-w-4xl mx-auto">
          {/* Hero Section */}
          {step === 1 && (
            <>
              <div className="text-center mb-12">
                <p className="text-[11px] font-bold text-[#2F80ED] uppercase tracking-[1.2px] mb-4">
                  ACCOUNTABILITY DECLARATION
                </p>
                <h1 className="text-[40px] font-bold text-[#102A43] mb-4 leading-tight">
                  Publicly verifiable proof that your organization uses AI responsibly
                </h1>
                <p className="text-[18px] text-[#64748B] max-w-2xl mx-auto">
                  Organizations that complete the AASC Accountability Declaration are listed in the public registry, 
                  receive an embeddable accountability badge, and renew annually.
                </p>
              </div>

              {/* Single Tier Card */}
              <div className="bg-white border border-[#E2E8F0] rounded-[10px] p-8 mb-8">
                <div className="text-center mb-8">
                  <p className="text-[48px] font-bold text-[#102A43] mb-2">$997</p>
                  <p className="text-[16px] text-[#64748B]">per year</p>
                </div>

                <div className="grid md:grid-cols-3 gap-6 mb-8">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-[#2F80ED]/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <List className="text-[#2F80ED]" size={20} />
                    </div>
                    <div>
                      <p className="font-semibold text-[#102A43] text-[15px]">Public Registry Listing</p>
                      <p className="text-[13px] text-[#64748B]">Visible to clients, auditors, and procurement teams</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-[#2F80ED]/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <Shield className="text-[#2F80ED]" size={20} />
                    </div>
                    <div>
                      <p className="font-semibold text-[#102A43] text-[15px]">Embeddable Badge</p>
                      <p className="text-[13px] text-[#64748B]">Display your accountability status on your website</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-[#2F80ED]/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <RefreshCw className="text-[#2F80ED]" size={20} />
                    </div>
                    <div>
                      <p className="font-semibold text-[#102A43] text-[15px]">Annual Renewal</p>
                      <p className="text-[13px] text-[#64748B]">Maintain current status with yearly renewal</p>
                    </div>
                  </div>
                </div>

                <div className="bg-[#F9FAFB] border border-[#E2E8F0] rounded-[8px] p-4 mb-8">
                  <p className="text-[12px] text-[#64748B]">
                    <strong className="text-[#102A43]">Important:</strong> Declaration confirms documented accountability 
                    with defined foundational control standards. Declaration is conditional and revocable. 
                    Does not guarantee performance. Does not imply regulatory approval. Does not transfer liability.
                  </p>
                </div>

                <div className="border-t border-[#E2E8F0] pt-6">
                  <p className="text-[12px] font-bold text-[#64748B] uppercase tracking-wide mb-3">Requirements</p>
                  <ul className="grid md:grid-cols-2 gap-2 text-[14px] text-[#64748B]">
                    <li className="flex items-center gap-2">
                      <CheckCircle className="text-[#27AE60]" size={16} />
                      Defined scope statement
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="text-[#27AE60]" size={16} />
                      Responsibility domain mapping (AI-ROI-1.0)
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="text-[#27AE60]" size={16} />
                      Human escalation structure (AI-HITL-1.0)
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="text-[#27AE60]" size={16} />
                      Incident response documentation (AI-READ-1.0)
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="text-[#27AE60]" size={16} />
                      Change control documentation (AI-PERF-1.0)
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="text-[#27AE60]" size={16} />
                      Client disclosure acknowledgment (AI-DISC-1.0)
                    </li>
                  </ul>
                </div>

                <button
                  onClick={() => setStep(2)}
                  className="w-full mt-8 bg-[#2F80ED] text-white font-semibold py-4 rounded-[7px] hover:bg-[#2563eb] transition-colors flex items-center justify-center gap-2"
                  data-testid="start-declaration-btn"
                >
                  Start Your Declaration
                  <ArrowRight size={18} />
                </button>
              </div>

              {/* Not ready yet? */}
              <div className="text-center">
                <p className="text-[14px] text-[#64748B] mb-2">
                  Not ready for the Declaration yet?
                </p>
                <p className="text-[14px] text-[#64748B]">
                  Start with the{' '}
                  <Link to="/assessment/form" className="text-[#2F80ED] hover:underline">
                    free AI Accountability Score
                  </Link>
                  {' '}or get the{' '}
                  <Link to="/documentation-system" className="text-[#2F80ED] hover:underline">
                    Documentation System ($97)
                  </Link>
                  {' '}to prepare.
                </p>
              </div>
            </>
          )}

          {/* Step 2: Organization Details */}
          {step === 2 && (
            <div className="space-y-8">
              {/* Progress indicator */}
              <div className="flex items-center justify-center gap-4 mb-8">
                {[1, 2, 3].map((s) => (
                  <div key={s} className="flex items-center gap-2">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                      step >= s + 1 ? 'bg-[#2F80ED] text-white' : 'bg-[#E2E8F0] text-[#64748B]'
                    }`}>
                      {s}
                    </div>
                    {s < 3 && <div className={`w-12 h-0.5 ${step > s + 1 ? 'bg-[#2F80ED]' : 'bg-[#E2E8F0]'}`} />}
                  </div>
                ))}
              </div>

              <div className="text-center">
                <h1 className="text-[32px] font-bold text-[#102A43] mb-4">
                  Organization Details
                </h1>
                <p className="text-[16px] text-[#64748B]">
                  This information will be displayed in the public registry.
                </p>
              </div>

              <div className="bg-white border border-[#E2E8F0] rounded-[10px] p-6 space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="legal_name" className="text-[#102A43]">Legal Name *</Label>
                    <Input
                      id="legal_name"
                      name="legal_name"
                      value={formData.legal_name}
                      onChange={handleInputChange}
                      placeholder="Acme AI Solutions LLC"
                      className="mt-1"
                      data-testid="input-legal-name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="display_name" className="text-[#102A43]">Display Name *</Label>
                    <Input
                      id="display_name"
                      name="display_name"
                      value={formData.display_name}
                      onChange={handleInputChange}
                      placeholder="Acme AI Solutions"
                      className="mt-1"
                      data-testid="input-display-name"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="website" className="text-[#102A43]">Website</Label>
                    <Input
                      id="website"
                      name="website"
                      value={formData.website}
                      onChange={handleInputChange}
                      placeholder="https://example.com"
                      className="mt-1"
                      data-testid="input-website"
                    />
                  </div>
                  <div>
                    <Label htmlFor="contact_name" className="text-[#102A43]">Contact Name *</Label>
                    <Input
                      id="contact_name"
                      name="contact_name"
                      value={formData.contact_name}
                      onChange={handleInputChange}
                      placeholder="John Smith"
                      className="mt-1"
                      data-testid="input-contact-name"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="contact_email" className="text-[#102A43]">Contact Email *</Label>
                    <Input
                      id="contact_email"
                      name="contact_email"
                      type="email"
                      value={formData.contact_email}
                      onChange={handleInputChange}
                      placeholder="contact@example.com"
                      className="mt-1"
                      data-testid="input-contact-email"
                    />
                  </div>
                </div>

                <div className="border-t border-[#E2E8F0] pt-6">
                  <h3 className="font-semibold text-[#102A43] mb-4">Create Account Password</h3>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="password" className="text-[#102A43]">Password *</Label>
                      <Input
                        id="password"
                        name="password"
                        type="password"
                        value={formData.password}
                        onChange={handleInputChange}
                        placeholder="Minimum 8 characters"
                        className="mt-1"
                        data-testid="input-password"
                      />
                    </div>
                    <div>
                      <Label htmlFor="confirmPassword" className="text-[#102A43]">Confirm Password *</Label>
                      <Input
                        id="confirmPassword"
                        name="confirmPassword"
                        type="password"
                        value={formData.confirmPassword}
                        onChange={handleInputChange}
                        placeholder="Confirm password"
                        className="mt-1"
                        data-testid="input-confirm-password"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-between">
                <button
                  onClick={() => setStep(1)}
                  className="border border-[#E2E8F0] text-[#64748B] px-6 py-3 rounded-[7px] hover:bg-[#F9FAFB] transition-colors flex items-center gap-2"
                >
                  <ArrowLeft size={18} /> Back
                </button>
                <button
                  onClick={() => setStep(3)}
                  className="bg-[#2F80ED] text-white font-semibold px-6 py-3 rounded-[7px] hover:bg-[#2563eb] transition-colors flex items-center gap-2"
                >
                  Continue <ArrowRight size={18} />
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Documentation */}
          {step === 3 && (
            <div className="space-y-8">
              {/* Progress indicator */}
              <div className="flex items-center justify-center gap-4 mb-8">
                {[1, 2, 3].map((s) => (
                  <div key={s} className="flex items-center gap-2">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                      step >= s + 1 ? 'bg-[#2F80ED] text-white' : 'bg-[#E2E8F0] text-[#64748B]'
                    }`}>
                      {s}
                    </div>
                    {s < 3 && <div className={`w-12 h-0.5 ${step > s + 1 ? 'bg-[#2F80ED]' : 'bg-[#E2E8F0]'}`} />}
                  </div>
                ))}
              </div>

              <div className="text-center">
                <h1 className="text-[32px] font-bold text-[#102A43] mb-4">
                  Declaration Documentation
                </h1>
                <p className="text-[16px] text-[#64748B]">
                  Document your organization's accountability controls.
                </p>
              </div>

              <div className="bg-white border border-[#E2E8F0] rounded-[10px] p-6 space-y-6">
                <div>
                  <Label htmlFor="scope_statement" className="text-[#102A43]">Defined Scope Statement *</Label>
                  <p className="text-[12px] text-[#64748B] mb-2">
                    Describe the specific AI systems and workflows covered by this declaration.
                  </p>
                  <Textarea
                    id="scope_statement"
                    name="scope_statement"
                    value={formData.scope_statement}
                    onChange={handleInputChange}
                    placeholder="Document your AI system scope and operational boundaries..."
                    className="mt-1 min-h-[100px]"
                    data-testid="input-scope-statement"
                  />
                </div>

                <div>
                  <Label htmlFor="responsibility_domain" className="text-[#102A43]">Responsibility Domain Mapping (AI-ROI-1.0) *</Label>
                  <p className="text-[12px] text-[#64748B] mb-2">
                    Map operational responsibility boundaries and exclusions.
                  </p>
                  <Textarea
                    id="responsibility_domain"
                    name="responsibility_domain"
                    value={formData.responsibility_domain}
                    onChange={handleInputChange}
                    placeholder="Document responsibility domain boundaries..."
                    className="mt-1 min-h-[100px]"
                    data-testid="input-responsibility-domain"
                  />
                </div>

                <div>
                  <Label htmlFor="escalation_structure" className="text-[#102A43]">Human Escalation Structure (AI-HITL-1.0) *</Label>
                  <p className="text-[12px] text-[#64748B] mb-2">
                    Document human intervention triggers and decision-maker paths.
                  </p>
                  <Textarea
                    id="escalation_structure"
                    name="escalation_structure"
                    value={formData.escalation_structure}
                    onChange={handleInputChange}
                    placeholder="Document human escalation procedures..."
                    className="mt-1 min-h-[100px]"
                    data-testid="input-escalation-structure"
                  />
                </div>

                <div>
                  <Label htmlFor="incident_response_doc" className="text-[#102A43]">Incident Response Documentation (AI-READ-1.0) *</Label>
                  <p className="text-[12px] text-[#64748B] mb-2">
                    Procedures for identifying, logging, and remediating AI failures.
                  </p>
                  <Textarea
                    id="incident_response_doc"
                    name="incident_response_doc"
                    value={formData.incident_response_doc}
                    onChange={handleInputChange}
                    placeholder="Document incident response procedures..."
                    className="mt-1 min-h-[100px]"
                    data-testid="input-incident-response"
                  />
                </div>

                <div>
                  <Label htmlFor="change_control_doc" className="text-[#102A43]">Change Control Documentation (AI-PERF-1.0) *</Label>
                  <p className="text-[12px] text-[#64748B] mb-2">
                    Protocols for logging modifications to prompts, knowledge bases, and logic flows.
                  </p>
                  <Textarea
                    id="change_control_doc"
                    name="change_control_doc"
                    value={formData.change_control_doc}
                    onChange={handleInputChange}
                    placeholder="Document change control procedures..."
                    className="mt-1 min-h-[100px]"
                    data-testid="input-change-control"
                  />
                </div>

                <div className="border-t border-[#E2E8F0] pt-6">
                  <div className="flex items-start gap-3">
                    <Checkbox
                      id="client_disclosure_ack"
                      checked={formData.client_disclosure_ack}
                      onCheckedChange={(checked) => setFormData(prev => ({ ...prev, client_disclosure_ack: checked }))}
                      data-testid="checkbox-disclosure-ack"
                    />
                    <div>
                      <Label htmlFor="client_disclosure_ack" className="text-[14px] font-medium text-[#102A43]">
                        Client Disclosure Acknowledgment (AI-DISC-1.0) *
                      </Label>
                      <p className="text-[12px] text-[#64748B] mt-1">
                        I confirm that clear-language disclosure of AI system capabilities, limitations, 
                        and operational scope is provided to end-clients.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-between">
                <button
                  onClick={() => setStep(2)}
                  className="border border-[#E2E8F0] text-[#64748B] px-6 py-3 rounded-[7px] hover:bg-[#F9FAFB] transition-colors flex items-center gap-2"
                >
                  <ArrowLeft size={18} /> Back
                </button>
                <button
                  onClick={() => setStep(4)}
                  className="bg-[#2F80ED] text-white font-semibold px-6 py-3 rounded-[7px] hover:bg-[#2563eb] transition-colors flex items-center gap-2"
                >
                  Continue <ArrowRight size={18} />
                </button>
              </div>
            </div>
          )}

          {/* Step 4: Review & Submit */}
          {step === 4 && (
            <div className="space-y-8">
              {/* Progress indicator */}
              <div className="flex items-center justify-center gap-4 mb-8">
                {[1, 2, 3].map((s) => (
                  <div key={s} className="flex items-center gap-2">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                      step >= s + 1 ? 'bg-[#2F80ED] text-white' : 'bg-[#E2E8F0] text-[#64748B]'
                    }`}>
                      {s}
                    </div>
                    {s < 3 && <div className={`w-12 h-0.5 ${step > s + 1 ? 'bg-[#2F80ED]' : 'bg-[#E2E8F0]'}`} />}
                  </div>
                ))}
              </div>

              <div className="text-center">
                <h1 className="text-[32px] font-bold text-[#102A43] mb-4">
                  Review & Submit
                </h1>
                <p className="text-[16px] text-[#64748B]">
                  Please review your application and accept the required acknowledgments.
                </p>
              </div>

              {/* Summary */}
              <div className="bg-white border border-[#E2E8F0] rounded-[10px] p-6">
                <h3 className="font-semibold text-[#102A43] mb-4">Application Summary</h3>
                
                <div className="grid md:grid-cols-2 gap-4 text-[14px]">
                  <div>
                    <p className="text-[#64748B]">Application Type</p>
                    <p className="font-medium text-[#102A43]">AASC Accountability Declaration</p>
                  </div>
                  <div>
                    <p className="text-[#64748B]">Annual Fee</p>
                    <p className="font-medium text-[#102A43]">$997 USD</p>
                  </div>
                  <div>
                    <p className="text-[#64748B]">Organization</p>
                    <p className="font-medium text-[#102A43]">{formData.display_name || formData.legal_name}</p>
                  </div>
                  <div>
                    <p className="text-[#64748B]">Contact Email</p>
                    <p className="font-medium text-[#102A43]">{formData.contact_email}</p>
                  </div>
                </div>

                <div className="mt-4 pt-4 border-t border-[#E2E8F0]">
                  <p className="text-[#64748B] text-[14px] mb-2">Standards ({standards.length})</p>
                  <div className="flex flex-wrap gap-2">
                    {standards.map(std => (
                      <span key={std.id} className="text-[12px] bg-[#F9FAFB] border border-[#E2E8F0] px-2 py-1 rounded">
                        {std.id}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              {/* Acknowledgments */}
              <div className="bg-white border border-[#E2E8F0] rounded-[10px] p-6 space-y-4">
                <h3 className="font-semibold text-[#102A43]">Required Acknowledgments</h3>
                
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="reviewed_standards"
                    checked={acknowledgments.reviewed_standards}
                    onCheckedChange={(checked) => setAcknowledgments(prev => ({ ...prev, reviewed_standards: checked }))}
                    data-testid="ack-reviewed-standards"
                  />
                  <div>
                    <Label htmlFor="reviewed_standards" className="text-[14px]">
                      I have reviewed the official AASC standards and scope definitions.
                    </Label>
                    <Link 
                      to="/standards"
                      className="text-[12px] text-[#2F80ED] hover:underline flex items-center gap-1 mt-1"
                    >
                      View Standards Index <ExternalLink size={12} />
                    </Link>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Checkbox
                    id="understood_limitations"
                    checked={acknowledgments.understood_limitations}
                    onCheckedChange={(checked) => setAcknowledgments(prev => ({ ...prev, understood_limitations: checked }))}
                    data-testid="ack-limitations"
                  />
                  <Label htmlFor="understood_limitations" className="text-[14px]">
                    I understand that declaration confirms documented accountability with defined foundational control standards. 
                    Declaration is conditional and revocable. Declaration does not guarantee performance, imply regulatory approval, 
                    or transfer liability.
                  </Label>
                </div>

                <div className="flex items-start gap-3">
                  <Checkbox
                    id="accept_terms"
                    checked={acknowledgments.accept_terms}
                    onCheckedChange={(checked) => setAcknowledgments(prev => ({ ...prev, accept_terms: checked }))}
                    data-testid="ack-terms"
                  />
                  <div>
                    <Label htmlFor="accept_terms" className="text-[14px]">
                      I accept the Terms of Service and Privacy Policy.
                    </Label>
                    <div className="flex gap-4 mt-1">
                      <Link 
                        to="/terms-of-service"
                        className="text-[12px] text-[#2F80ED] hover:underline flex items-center gap-1"
                      >
                        Terms of Service <ExternalLink size={12} />
                      </Link>
                      <Link 
                        to="/privacy-policy"
                        className="text-[12px] text-[#2F80ED] hover:underline flex items-center gap-1"
                      >
                        Privacy Policy <ExternalLink size={12} />
                      </Link>
                    </div>
                  </div>
                </div>
              </div>

              {error && (
                <div className="bg-[#FEF2F2] border border-[#FECACA] rounded-[10px] p-4 flex items-center gap-3">
                  <AlertTriangle className="text-[#B91C1C] flex-shrink-0" size={20} />
                  <p className="text-[14px] text-[#B91C1C]">{error}</p>
                </div>
              )}

              <div className="flex justify-between">
                <button
                  onClick={() => setStep(3)}
                  className="border border-[#E2E8F0] text-[#64748B] px-6 py-3 rounded-[7px] hover:bg-[#F9FAFB] transition-colors flex items-center gap-2"
                >
                  <ArrowLeft size={18} /> Back
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={loading}
                  className="bg-[#2F80ED] text-white font-semibold px-6 py-3 rounded-[7px] hover:bg-[#2563eb] transition-colors disabled:opacity-50 flex items-center gap-2"
                  data-testid="submit-application"
                >
                  {loading ? 'Submitting...' : 'Submit & Proceed to Payment'}
                </button>
              </div>
            </div>
          )}
        </div>
      </main>

      <MainFooter />
    </div>
  );
}
